import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
}

// Map page labels to their corresponding icons
const getIconForLabel = (label: string): string => {
  const iconMap: Record<string, string> = {
    'Home': 'ri-home-line',
    'About': 'ri-information-line',
    'Services': 'ri-tools-line',
    'Insulation Services': 'ri-tools-line',
    'FAQ': 'ri-question-line',
    'Residential': 'ri-home-4-line',
    'Commercial': 'ri-building-line',
    'Projects': 'ri-gallery-line',
    'Closed Cell Foam': 'ri-drop-line',
    'Open Cell Foam': 'ri-bubble-chart-line'
  };
  return iconMap[label] || '';
};

// Updated to use schema.org breadcrumb JSON‑LD for better SEO
export default function Breadcrumb({ items }: BreadcrumbProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Inject structured data for navigation breadcrumbs
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: item.label,
        item: item.href ? window.location.origin + item.href : window.location.href
      }))
    };
    let scriptTag = document.querySelector('script[data-schema="breadcrumb"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-schema', 'breadcrumb');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schema);
  }, [items]);

  return (
    <nav
      className={`fixed top-24 sm:top-28 left-0 right-0 z-40 bg-white/80 backdrop-blur-sm border-b border-gray-200/50 py-2 sm:py-3 transition-all duration-500 ease-in-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
      }`}
      aria-label="Breadcrumb"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ol className="flex items-center space-x-2 text-sm">
          {items.map((item, index) => {
            const icon = getIconForLabel(item.label);
            return (
              <li key={index} className="flex items-center">
                {index > 0 && (
                  <i className="ri-arrow-right-s-line text-gray-400/70 mx-2 text-base"></i>
                )}
                {item.href ? (
                  <Link
                    to={item.href}
                    className="text-gray-500 hover:text-blue-600 transition-colors cursor-pointer font-medium flex items-center gap-1.5"
                  >
                    {icon && <i className={`${icon} text-base`}></i>}
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-gray-700 font-medium flex items-center gap-1.5">
                    {icon && <i className={`${icon} text-base`}></i>}
                    {item.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
